import { useEffect, useState } from 'react';
import {
  getDataset,
  listDatasets,
  listVersions,
  type ColumnSchema,
  type Dataset,
  type DatasetVersion,
  type TransformStep,
} from '../../api/datasets';
import { Alert, Button, Input } from '../ui';
import './StepForm.css';

export interface JoinStepFormProps {
  /** Columns of the data as it stands at this point in the recipe. */
  columns: ColumnSchema[];
  /** The dataset being prepared — you cannot join it to itself here. */
  currentDatasetId: string;
  onAdd: (step: TransformStep) => void;
  onCancel: () => void;
}

/**
 * The join step.
 *
 * It needs its own form because the choices depend on each other: pick a
 * dataset, then its columns become available to match against. Whatever is
 * chosen, the backend re-checks that the dataset belongs to the same person
 * before it loads a single row.
 */
export function JoinStepForm({ columns, currentDatasetId, onAdd, onCancel }: JoinStepFormProps) {
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [datasetId, setDatasetId] = useState('');
  const [version, setVersion] = useState<DatasetVersion | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [how, setHow] = useState<'inner' | 'left'>('inner');
  const [leftColumn, setLeftColumn] = useState('');
  const [rightColumn, setRightColumn] = useState('');
  const [prefix, setPrefix] = useState('');

  useEffect(() => {
    void listDatasets().then((page) =>
      setDatasets(
        page.results.filter(
          (entry) => entry.id !== currentDatasetId && entry.latest_version?.status === 'READY',
        ),
      ),
    );
  }, [currentDatasetId]);

  useEffect(() => {
    if (!datasetId) {
      setVersion(null);
      return;
    }
    setIsLoading(true);
    void Promise.all([getDataset(datasetId), listVersions(datasetId)])
      .then(([, versions]) => {
        const ready = versions.find((entry) => entry.status === 'READY') ?? null;
        setVersion(ready);
        setRightColumn('');
      })
      .finally(() => setIsLoading(false));
  }, [datasetId]);

  const canAdd = Boolean(version && leftColumn && rightColumn);

  return (
    <div className="sc-stepform">
      <label className="sc-stepform__field">
        <span>Join to</span>
        <select value={datasetId} onChange={(event) => setDatasetId(event.target.value)}>
          <option value="">Choose a dataset…</option>
          {datasets.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </select>
      </label>

      {datasets.length === 0 && (
        <p className="sc-stepform__description">
          You need a second dataset to join to. Upload one first.
        </p>
      )}

      <label className="sc-stepform__field">
        <span>Keep rows that</span>
        <select value={how} onChange={(event) => setHow(event.target.value as 'inner' | 'left')}>
          <option value="inner">match in both (inner join)</option>
          <option value="left">are in this dataset, matched or not (left join)</option>
        </select>
      </label>

      <label className="sc-stepform__field">
        <span>Match this column</span>
        <select value={leftColumn} onChange={(event) => setLeftColumn(event.target.value)}>
          <option value="">Choose a column…</option>
          {columns.map((column) => (
            <option key={column.name} value={column.name}>
              {column.source_name}
            </option>
          ))}
        </select>
      </label>

      <label className="sc-stepform__field">
        <span>…to this one</span>
        <select
          value={rightColumn}
          onChange={(event) => setRightColumn(event.target.value)}
          disabled={!version || isLoading}
        >
          <option value="">
            {isLoading
              ? 'Loading columns…'
              : version
                ? 'Choose a column…'
                : 'Choose a dataset first'}
          </option>
          {version?.column_schema.map((column) => (
            <option key={column.name} value={column.name}>
              {column.source_name}
            </option>
          ))}
        </select>
      </label>

      <Input
        label="Prefix for the added columns (optional)"
        value={prefix}
        hint="Keeps the new columns recognisable, e.g. shop_."
        onChange={(event) => setPrefix(event.target.value)}
      />

      <Alert tone="info" title="Simple Coffee checks the join before running it">
        You will see how many rows matched, and a warning if the match would duplicate rows.
      </Alert>

      <div className="sc-stepform__actions">
        <Button
          disabled={!canAdd}
          onClick={() =>
            onAdd({
              op: 'join',
              how,
              with_version_id: version!.id,
              on: [{ left: leftColumn, right: rightColumn }],
              ...(prefix ? { prefix } : {}),
            })
          }
        >
          Add step
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
